import { useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "../api";
import { fields as schemaFields, type ContentField, type EntityType } from "../content/model";
import { AddForm, ConfirmDialog, IconButton, SectionHead } from "./Section";
import { useQueryParam, writeQueries } from "../urlState";

// Content, the fifth product (the Content design: curriculum management for content providers).
// A provider writes curricula, the rubrics its lessons are scored on, and the tasks students are
// asked to do; publishes them as versions; makes them free or licenses them to districts.
//
// Every write is a database function that asks the access engine (0105, 0107, 0111), so this screen
// decides nothing about who may do what. It shows what the server says (`may_edit`,
// `may_publish`, `configures`) and, where it does not say, offers the action and shows the
// server's refusal in its own words. Curriculum reviewers see everything and change nothing.
//
// First version (deliberately simple): the text, prose and number fields of a unit or lesson are
// edited here; the structured ones (handout blocks, texts, resources, standards) are shown and kept
// as they are. Order changes with up and down, not dragging.
//
// What is open (provider, curriculum, node, rubric, task) lives in the query string, so a view can
// be linked and survives a reload.

// ------------------------------------------------------------------------------------ types

export interface Provider { provider_id: string; name: string; configures: boolean }
interface CurriculumRow { resource_id: string; name: string; retired: boolean; latest: string | null;
                          name_tag: string | null; published_at: string | null; editions: number }
export type NodeState = "new" | "changed" | "kept" | null;
export interface Node {
  resource_id: string; kind: "curriculum" | "unit" | "lesson"; name: string;
  body: Record<string, unknown>; task_id: string | null; task_version_id: string | null;
  rubric_version_ids: string[]; rigorous: boolean; retired: boolean; state: NodeState; shows: string | null; children: Node[];
}
interface Removed { resource_id: string; kind: string; name: string; parent_id: string }
interface CurriculumView { curriculum: Node; may_edit: boolean; may_publish: boolean;
                           removed: Removed[]; problems: string[]; may_be_a_fix: boolean }
export interface Version { version_id: string; version: string; edition_id: string | null;
                           name_tag: string | null; notes: string | null; published_at: string;
                           withdrawn_at: string | null }
export interface License { edition_id: string; major: number; district_id: string;
                           district: string | null; relation: string; valid_from: string;
                           valid_until: string | null }
interface Writer { relationship_id: string; subject_type: string; subject_id: string; name: string }
interface Change { resource_id: string; kind: string; name: string; unit_id: string | null;
                   state: "added" | "removed" | "changed" | "kept" }
interface RubricRow { rubric_id: string; name: string; grade_band: string | null; status: string;
                      trait_term: string | null; published: number | null; draft_id: string | null }
export interface RubricTrait {
  trait_id: string; trait_label: string; learning_standard_item: string;
  scale_categories: (number | string)[]; trait_major: number; major_published: boolean;
}
interface RubricVersion { rubric_version_id: string; version: number; status: string;
                          name_tag: string | null; notes: string | null; published_at: string | null;
                          traits: RubricTrait[] }
interface RubricView { rubric_id: string; name: string; grade_band: string | null; status: string;
                       trait_term: string | null; versions: RubricVersion[] }
interface TraitRow { trait_id: string; trait_label: string; learning_standard_item: string;
                     grade_band: string; scale_categories: (number | string)[]; version: string | null;
                     has_draft: boolean; rubrics: number }
/** A level's descriptor: text, or a list of clauses. */
export type Descriptor = string | string[];
export interface Augmented { general?: string; levels?: Record<string, string> }
export interface TraitVersion {
  trait_version_id: string; version: string; status: string; model_label: string;
  descriptors: Record<string, Descriptor>; augmented: Augmented; change_note: string | null;
  reason: string | null; based_on: string | null; created_at: string;
}
interface TraitView { trait_id: string; trait_label: string; learning_standard_item: string;
                      grade_band: string; scale_categories: (number | string)[];
                      versions: TraitVersion[];
                      rubrics: { rubric_id: string; name: string; rubric_version_id: string;
                                 version: number; status: string; trait_major: number }[] }
interface TaskRow { task_id: string; name: string; grade_band: string | null;
                    has_statement: boolean }
interface TaskText { title: string; citation: string | null; body: string }
interface TaskView { task_id: string; name: string; grade_band: string | null;
                     statement: string | null; texts: TaskText[] }

// ------------------------------------------------------------------------------ pure helpers

/** The server's sentence for a refusal, or a plain one. */
export function said(e: unknown): string {
  return e instanceof ApiError ? (e.detail ?? e.message) : "That did not work.";
}

/** Where `id` goes when moved up or down among its live siblings: a 1-based position, or null
 *  when it is already at that end. */
export function movedTo(siblings: string[], id: string, dir: -1 | 1): number | null {
  const at = siblings.indexOf(id);
  if (at < 0) return null;
  const to = at + dir;
  return to < 0 || to >= siblings.length ? null : to + 1;
}

/** Units and lessons since the last version, by state: what publishing would carry. */
export function changeCounts(root: Node, removed: { kind: string }[]) {
  const out = { new: 0, changed: 0, kept: 0, removed: removed.length };
  const walk = (n: Node) => {
    if (n.kind !== "curriculum" && !n.retired && n.state) out[n.state] += 1;
    n.children.forEach(walk);
  };
  walk(root);
  return out;
}

/** A curriculum's editions, newest first: one per major version, from its versions. */
export function editionsOf(versions: Version[]): { edition_id: string; major: number }[] {
  const seen = new Map<string, number>();
  for (const v of versions) {
    if (v.edition_id && !seen.has(v.edition_id)) seen.set(v.edition_id, Number(v.version.split(".")[0]));
  }
  return [...seen].map(([edition_id, major]) => ({ edition_id, major })).sort((a, b) => b.major - a.major);
}

const SIMPLE = new Set(["text", "prose", "number"]);

/** The fields of a unit or lesson this version edits (text, prose, number), and the rest, shown
 *  and kept as they are. A curriculum has a name only. */
export function editableFields(kind: Node["kind"]): { simple: ContentField[]; other: ContentField[] } {
  if (kind === "curriculum") return { simple: [], other: [] };
  const all = schemaFields(kind as EntityType);
  return { simple: all.filter((f) => SIMPLE.has(f.type)), other: all.filter((f) => !SIMPLE.has(f.type)) };
}

/** The body to save: what was there, with the edited simple fields. An emptied field is removed;
 *  a number field is a number. */
export function bodyWith(body: Record<string, unknown>, edits: Record<string, string>,
                         simple: ContentField[]): Record<string, unknown> {
  const out: Record<string, unknown> = { ...body };
  for (const f of simple) {
    if (!(f.name in edits)) continue;
    const raw = edits[f.name].trim();
    if (raw === "") delete out[f.name];
    else out[f.name] = f.type === "number" && !Number.isNaN(Number(raw)) ? Number(raw) : raw;
  }
  return out;
}

/** "1, 2, 3, 4" -> [1, 2, 3, 4]: a scale's levels, in order. Levels are numbers. */
export function levelsFrom(text: string): number[] {
  return text.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean).map(Number)
    .filter((n) => !Number.isNaN(n));
}

/** A descriptor's clauses: one for text, each of a list. */
export function clausesOf(value: Descriptor | undefined): string[] {
  if (value === undefined || value === null) return [];
  return (Array.isArray(value) ? value : [value]).map((c) => c.trim()).filter(Boolean);
}

/** A descriptor as it is edited: a clause per line. */
export function textOf(value: Descriptor | undefined): string {
  return clausesOf(value).join("\n");
}

/** What was typed, as a descriptor: one line is text, several lines are a list of clauses. */
export function descriptorFrom(text: string): Descriptor {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  return lines.length === 1 ? lines[0] : lines;
}

const canonical = (d: Record<string, Descriptor>) =>
  JSON.stringify(Object.keys(d).sort().map((k) => [k, d[k]]));

/** The number a trait's draft publishes as, decided by what changed (docs/DESIGN.md, "Versions"):
 *  a minor of the major it started from while its label and descriptors are that major's x.0's;
 *  the next major once they are not; 1.0 for a new trait. The database decides the same way. */
export function numberFor(versions: TraitVersion[], draft: TraitVersion, label: string,
                          descriptors: Record<string, Descriptor>): string {
  const done = versions.filter((v) => v.status !== "draft");
  const base = done.find((v) => v.trait_version_id === draft.based_on);
  if (!base) return "1.0";
  const major = (v: TraitVersion) => Number(v.version.split(".")[0]);
  const minor = (v: TraitVersion) => Number(v.version.split(".")[1]);
  const x0 = done.find((v) => major(v) === major(base) && minor(v) === 0);
  if (x0 && x0.model_label === label && canonical(x0.descriptors) === canonical(descriptors))
    return `${major(base)}.${Math.max(...done.filter((v) => major(v) === major(base)).map(minor)) + 1}`;
  return `${Math.max(...done.map(major)) + 1}.0`;
}

/** What a structured field holds, in a few words, for a field this version shows but does not
 *  edit. */
export function summarize(value: unknown): string {
  if (value === undefined || value === null || value === "") return "—";
  if (Array.isArray(value)) return value.length === 0 ? "—" : `${value.length} item${value.length === 1 ? "" : "s"}`;
  if (typeof value === "object") return `${Object.keys(value as object).length} entries`;
  return String(value);
}

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : "");

// ---------------------------------------------------------------------------------- loading

type Load<T> = { data: T | null; error: string | null };

function useGet<T>(path: string | null, key = 0): Load<T> {
  const [state, setState] = useState<Load<T>>({ data: null, error: null });
  useEffect(() => {
    if (!path) { setState({ data: null, error: null }); return; }
    let live = true;
    setState((s) => ({ data: s.data, error: null }));
    api.get<T>(path).then(
      (data) => { if (live) setState({ data, error: null }); },
      (e) => { if (live) setState({ data: null, error: said(e) }); });
    return () => { live = false; };
  }, [path, key]);
  return state;
}

function notReady<T>(load: Load<T>, what: string): ReactNode {
  if (load.error) return <p className="inc-error">{load.error}</p>;
  if (!load.data) return <p className="muted dots">loading {what}</p>;
  return null;
}

/** Run one write; its refusal becomes the error shown. Resolves true when it worked. */
function useAct(onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function act(fn: () => Promise<unknown>): Promise<boolean> {
    setBusy(true); setError(null);
    try { await fn(); onDone(); return true; }
    catch (e) { setError(said(e)); return false; }
    finally { setBusy(false); }
  }
  return { busy, error, setError, act };
}

/** The providers the caller sees, and the one open (in the query string). */
function useProvider() {
  const load = useGet<{ providers: Provider[] }>("/content/providers");
  const [chosen, setChosen] = useQueryParam("provider");
  const providers = load.data?.providers ?? [];
  const provider = providers.find((p) => p.provider_id === chosen) ?? providers[0] ?? null;
  const choose = (id: string) => setChosen(id);
  return { load, providers, provider, choose };
}

function ProviderBar({ providers, provider, choose }: {
  providers: Provider[]; provider: Provider; choose: (id: string) => void }) {
  if (providers.length < 2) return <p className="gl-lede">{provider.name}</p>;
  return (
    <p className="gl-lede">
      <label>Provider{" "}
        <select value={provider.provider_id} onChange={(e) => choose(e.target.value)}>
          {providers.map((p) => <option key={p.provider_id} value={p.provider_id}>{p.name}</option>)}
        </select>
      </label>
    </p>
  );
}

/** The "+" beside a page's title (docs/DESIGN.md, "Adding another"), and whether its form is open. */
interface Adding { adding: boolean; setAdding: (open: boolean) => void }

function Page({ title, lede, addLabel, children }: {
  title: string; lede?: string; addLabel?: string; children: (p: Provider, add: Adding) => ReactNode }) {
  const { load, providers, provider, choose } = useProvider();
  const [adding, setAdding] = useState(false);
  return (
    <div className="gl sc ct">
      {addLabel && provider
        ? <SectionHead title={title} level={2} addLabel={addLabel} adding={adding} onAdd={() => setAdding(!adding)} />
        : <h2>{title}</h2>}
      {notReady(load, "providers") ?? (!provider
        ? <p className="sc-mut">You see no content provider's content. A provider's configurators,
            or Preva, give you a place in one.</p>
        : <>
            <ProviderBar providers={providers} provider={provider} choose={choose} />
            {lede && <p className="sc-mut ct-lede">{lede}</p>}
            {children(provider, { adding, setAdding })}
          </>)}
    </div>
  );
}

const STATE_LABEL: Record<string, string> = { new: "new", changed: "changed", kept: "published" };

function StateChip({ state, shows }: { state: NodeState; shows: string | null }) {
  if (!state) return null;
  return <span className={`sc-chip ct-${state}`}>{STATE_LABEL[state]}{shows ? ` ${shows}` : ""}</span>;
}

// ============================================================================== Curricula

export function ContentCurricula() {
  const [open] = useQueryParam("curriculum");
  const go = (id: string | null) => writeQueries({ curriculum: id, node: null });
  return (
    <Page title="Curricula">
      {(p) => open ? <CurriculumEditor id={open} provider={p} onBack={() => go(null)} />
                   : <CurriculumList provider={p} onOpen={go} />}
    </Page>
  );
}

function CurriculumList({ provider, onOpen }: { provider: Provider; onOpen: (id: string) => void }) {
  const [key, setKey] = useState(0);
  const load = useGet<{ curricula: CurriculumRow[] }>(
    `/content/curricula?provider_id=${encodeURIComponent(provider.provider_id)}`, key);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const { busy, error, act } = useAct(() => setKey((k) => k + 1));
  async function create() {
    let made: string | null = null;
    if (await act(async () => {
      made = (await api.post<{ resource_id: string }>("/content/curricula",
        { provider_id: provider.provider_id, name })).resource_id;
    }) && made) onOpen(made);
  }
  return (
    <section className="pp-panel">
      <SectionHead title="Curricula" addLabel="Start a curriculum" adding={adding}
                   onAdd={() => setAdding(!adding)}
                   disabledWhy={provider.configures ? null : "Only the provider's configurators start one."} />
      {adding && (
        <AddForm saveLabel="Start it" missing={name.trim() ? null : "Enter a name."} busy={busy} error={error}
                 onSave={create} onCancel={() => setAdding(false)}>
          <label>Name<input value={name} autoFocus onChange={(e) => setName(e.target.value)} /></label>
        </AddForm>)}
      {notReady(load, "curricula") ?? (load.data!.curricula.length === 0
        ? <p className="sc-mut">None yet.</p>
        : <table className="tbl">
            <thead><tr><th>Curriculum</th><th>Latest version</th><th className="r">Editions</th></tr></thead>
            <tbody>
              {load.data!.curricula.map((c) => (
                <tr key={c.resource_id}>
                  <td><button type="button" className="btn-link" onClick={() => onOpen(c.resource_id)}>
                    {c.name}</button>{c.retired && <span className="sc-chip">retired</span>}</td>
                  <td>{c.latest ? `${c.latest}${c.name_tag ? ` · ${c.name_tag}` : ""} · ${day(c.published_at)}`
                                : <span className="sc-mut">not published</span>}</td>
                  <td className="r">{c.editions}</td>
                </tr>))}
            </tbody>
          </table>)}
    </section>
  );
}

type Pane = "draft" | "publish" | "versions" | "licenses" | "writers";
const PANES: [Pane, string][] = [["draft", "Draft"], ["publish", "Publish"], ["versions", "Versions"],
                                 ["licenses", "Licenses"], ["writers", "Writers"]];

function CurriculumEditor({ id, provider, onBack }: { id: string; provider: Provider; onBack: () => void }) {
  const [key, setKey] = useState(0);
  const reload = () => setKey((k) => k + 1);
  const load = useGet<CurriculumView>(`/content/curricula/${encodeURIComponent(id)}`, key);
  const [paneQ, setPane] = useQueryParam("pane");
  const pane = (paneQ as Pane | null) || "draft";
  const choose = (p: Pane) => setPane(p === "draft" ? null : p);
  const view = load.data;
  return (
    <section>
      <p><button type="button" className="btn-link" onClick={onBack}>← All curricula</button></p>
      {notReady(load, "the curriculum") ?? (
        <>
          <div className="sc-head">
            <h3 className="sc-title">{view!.curriculum.name}</h3>
            {!view!.may_edit && <span className="sc-chip">read only</span>}
          </div>
          <div className="ct-tabs" role="tablist">
            {PANES.map(([p, label]) => (
              <button key={p} type="button" role="tab" aria-selected={pane === p}
                      className={"btn sm" + (pane === p ? " primary" : "")} onClick={() => choose(p)}>
                {label}</button>))}
          </div>
          {pane === "draft" && <Draft view={view!} provider={provider} onChanged={reload} />}
          {pane === "publish" && <Publish id={id} view={view!} onChanged={reload} />}
          {pane === "versions" && <Versions id={id} mayPublish={view!.may_publish} reloadKey={key} />}
          {pane === "licenses" && <Licenses id={id} mayPublish={view!.may_publish} />}
          {pane === "writers" && <Writers id={id} mayPublish={view!.may_publish} />}
        </>)}
    </section>
  );
}

// ---------------------------------------------------------------------------------- draft

function find(root: Node, id: string | null): Node | null {
  if (!id) return null;
  if (root.resource_id === id) return root;
  for (const c of root.children) { const f = find(c, id); if (f) return f; }
  return null;
}

function parentOf(root: Node, id: string): Node | null {
  for (const c of root.children) {
    if (c.resource_id === id) return root;
    const f = parentOf(c, id);
    if (f) return f;
  }
  return null;
}

function Draft({ view, provider, onChanged }: { view: CurriculumView; provider: Provider; onChanged: () => void }) {
  const root = view.curriculum;
  const [nodeId, setNodeId] = useQueryParam("node");
  const open = (id: string) => setNodeId(id);
  const node = find(root, nodeId) ?? root;
  const live = (n: Node) => n.children.filter((c) => !c.retired);
  return (
    <div className="sc-two">
      <nav className="sc-tree ct-tree" aria-label="Units and lessons">
        <button type="button" className={"sc-trait" + (node === root ? " cur" : "")}
                onClick={() => open(root.resource_id)}>
          {root.name}<StateChip state={root.state} shows={root.shows} /></button>
        {live(root).length === 0 && <p className="sc-mut">No units yet.</p>}
        {live(root).map((u) => (
          <details key={u.resource_id} open>
            <summary>
              <button type="button" className={"sc-trait" + (node === u ? " cur" : "")}
                      onClick={(e) => { e.preventDefault(); open(u.resource_id); }}>
                {u.name}<StateChip state={u.state} shows={u.shows} /></button>
            </summary>
            <ul>
              {live(u).map((l) => (
                <li key={l.resource_id}>
                  <button type="button" className={"sc-trait" + (node === l ? " cur" : "")}
                          onClick={() => open(l.resource_id)}>
                    {l.name}{l.rigorous && <span className="sc-chip ct-rigorous" title="Counts toward measurement">counts</span>}
                    <StateChip state={l.state} shows={l.shows} /></button>
                </li>))}
              {live(u).length === 0 && <li className="sc-mut">No lessons.</li>}
            </ul>
          </details>))}
        {view.removed.length > 0 && (
          <p className="sc-mut">Removed since the last version: {view.removed.map((r) => r.name).join(", ")}</p>)}
      </nav>
      <section className="sc-pane">
        <NodeEditor key={node.resource_id} node={node} root={root} mayEdit={view.may_edit}
                    providerId={provider.provider_id} onChanged={onChanged} onOpen={open} />
      </section>
    </div>
  );
}

function NodeEditor({ node, root, mayEdit, providerId, onChanged, onOpen }: {
  node: Node; root: Node; mayEdit: boolean; providerId: string; onChanged: () => void;
  onOpen: (id: string) => void }) {
  const { simple, other } = editableFields(node.kind);
  const [name, setName] = useState(node.name);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState(false);
  const [childName, setChildName] = useState("");
  const [retiring, setRetiring] = useState(false);
  const { busy, error, act } = useAct(onChanged);
  const parent = node.kind === "curriculum" ? null : parentOf(root, node.resource_id);
  const siblings = parent ? parent.children.filter((c) => !c.retired).map((c) => c.resource_id) : [];
  const childKind = node.kind === "curriculum" ? "unit" : node.kind === "unit" ? "lesson" : null;
  const dirty = name !== node.name || Object.keys(edits).length > 0;
  // Save stays pressable and says what's missing (docs/DESIGN.md, "Forms").
  const [told, setTold] = useState(false);
  const missing = !name.trim() ? "Enter a name." : !dirty ? "Nothing has changed." : null;
  const value = (f: ContentField) => (f.name in edits ? edits[f.name]
                                      : node.body[f.name] === undefined ? "" : String(node.body[f.name]));
  const why = mayEdit ? null : "Only its writers and the provider's configurators change the draft.";

  function move(dir: -1 | 1) {
    const to = movedTo(siblings, node.resource_id, dir);
    if (to && parent) act(() => api.post(`/content/nodes/${node.resource_id}/move`,
                                         { parent_id: parent.resource_id, position: to }));
  }
  async function addChild() {
    let made: string | null = null;
    if (await act(async () => {
      made = (await api.post<{ resource_id: string }>("/content/nodes",
        { parent_id: node.resource_id, kind: childKind, name: childName })).resource_id;
    })) { setAdding(false); setChildName(""); if (made) onOpen(made); }
  }

  return (
    <div className="ct-node">
      <div className="sc-head">
        <h4 className="ct-kind">{node.kind}</h4>
        <StateChip state={node.state} shows={node.shows} />
        {parent && (
          <span className="ct-actions">
            <button type="button" className="btn sm" aria-label="Move up" title="Move up"
                    disabled={!mayEdit || busy || movedTo(siblings, node.resource_id, -1) === null}
                    onClick={() => move(-1)}>↑</button>
            <button type="button" className="btn sm" aria-label="Move down" title="Move down"
                    disabled={!mayEdit || busy || movedTo(siblings, node.resource_id, 1) === null}
                    onClick={() => move(1)}>↓</button>
            <IconButton icon="trash" size="row" danger label={`Retire this ${node.kind}`}
                        disabled={!mayEdit || busy} why={why} onClick={() => setRetiring(true)} />
          </span>)}
      </div>
      <div className="ct-fields">
        <label className="pp-field">Name
          <input value={name} disabled={!mayEdit} onChange={(e) => setName(e.target.value)} /></label>
        {simple.map((f) => (
          <label key={f.name} className="pp-field">{f.label}
            {f.type === "prose"
              ? <textarea rows={3} value={value(f)} disabled={!mayEdit}
                          onChange={(e) => setEdits({ ...edits, [f.name]: e.target.value })} />
              : <input value={value(f)} disabled={!mayEdit} inputMode={f.type === "number" ? "numeric" : undefined}
                       onChange={(e) => setEdits({ ...edits, [f.name]: e.target.value })} />}
          </label>))}
        {other.length > 0 && (
          <dl className="sc-kv ct-other">
            {other.map((f) => (
              <div key={f.name} className="sc-kv-row"><dt>{f.label}</dt><dd>{summarize(node.body[f.name])}</dd></div>))}
            <div className="sc-kv-row"><dt></dt><dd className="sc-mut">Kept as they are: edited in a later version of this screen.</dd></div>
          </dl>)}
        {mayEdit && (
          <div className="pp-actions">
            <button type="button" className="btn primary sm" disabled={busy}
                    onClick={() => {
                      setTold(!!missing);
                      if (missing) return;
                      act(() => api.put(`/content/nodes/${node.resource_id}`, {
                        kind: node.kind, name, body: bodyWith(node.body, edits, simple) }))
                        .then((ok) => { if (ok) setEdits({}); });
                    }}>{busy ? "Saving…" : "Save"}</button>
            {dirty && <button type="button" className="btn sm" disabled={busy}
                              onClick={() => { setName(node.name); setEdits({}); }}>Undo</button>}
            {told && missing && <span className="inc-error" role="alert">{missing}</span>}
          </div>)}
        {node.kind === "lesson" && <LessonScoring node={node} providerId={providerId} mayEdit={mayEdit}
                                                  onChanged={onChanged} />}
        {error && <p className="inc-error">{error}</p>}
      </div>
      {childKind && (
        <section className="pp-panel">
          <SectionHead title={childKind === "unit" ? "Units" : "Lessons"}
                       addLabel={`Add a ${childKind}`} adding={adding} onAdd={() => setAdding(!adding)}
                       disabledWhy={why} />
          {adding && (
            <AddForm saveLabel={`Add the ${childKind}`} missing={childName.trim() ? null : "Enter a name."} busy={busy}
                     error={error} onSave={addChild} onCancel={() => setAdding(false)}
                     note="It is added at the end; move it from there.">
              <label>Name<input value={childName} autoFocus onChange={(e) => setChildName(e.target.value)} /></label>
            </AddForm>)}
        </section>)}
      <ConfirmDialog open={retiring} title={`Retire “${node.name}”?`} confirmLabel="Retire" busy={busy}
                     onCancel={() => setRetiring(false)}
                     onConfirm={() => act(() => api.del(`/content/nodes/${node.resource_id}`))
                       .then(() => { setRetiring(false); if (parent) onOpen(parent.resource_id); })}>
        <p>It leaves the draft. Published versions keep it, and every class following one keeps
           teaching it; the next version you publish no longer has it.</p>
      </ConfirmDialog>
    </div>
  );
}

/** What a lesson's work is scored on: its task, its rubrics at their versions, and whether its
 *  scores count toward measurement (docs/DESIGN.md, "Naming a version"). A task's versions are the
 *  system's record and are not shown: a lesson given a task keeps its wording as it is then. */
function LessonScoring({ node, providerId, mayEdit, onChanged }: {
  node: Node; providerId: string; mayEdit: boolean; onChanged: () => void }) {
  const q = encodeURIComponent(providerId);
  const tasks = useGet<{ tasks: TaskRow[] }>(`/content/tasks?provider_id=${q}`);
  const rubrics = useGet<{ rubrics: RubricRow[] }>(`/content/rubrics?provider_id=${q}`);
  const task = useGet<TaskView>(node.task_id ? `/content/tasks/${encodeURIComponent(node.task_id)}` : null);
  const [changingTask, setChangingTask] = useState(false);
  const [addingRubric, setAddingRubric] = useState(false);
  const { busy, error, act } = useAct(() => { setChangingTask(false); setAddingRubric(false); onChanged(); });
  const why = mayEdit ? null : "Only its writers and the provider's configurators change the draft.";
  const setRubrics = (ids: string[]) =>
    act(() => api.put(`/content/nodes/${node.resource_id}/rubrics`, { rubric_version_ids: ids }));
  const versions = useRubricVersions(rubrics.data?.rubrics ?? []);
  const held = node.rubric_version_ids.map((id) => versions.find((v) => v.rubric_version_id === id)
                                                   ?? { rubric_version_id: id, rubric_id: "", name: id, version: 0 });
  const notHeld = (rubrics.data?.rubrics ?? []).filter((r) => r.published && !held.some((h) => h.rubric_id === r.rubric_id));
  return (
    <>
      <section className="pp-panel">
        <SectionHead title="Task" />
        <table className="tbl">
          <thead><tr><th>Task</th><th /></tr></thead>
          <tbody>
            <tr>
              <td>{node.task_id ? (task.data?.name ?? node.task_id) : <span className="sc-mut">None: not scored.</span>}</td>
              <td className="r">
                <IconButton icon="pencil" size="row" label="Change the task" pressed={changingTask}
                            disabled={!mayEdit || busy} why={why} onClick={() => setChangingTask(!changingTask)} />
              </td>
            </tr>
          </tbody>
        </table>
        {changingTask && (
          <TaskPicker tasks={tasks.data?.tasks ?? []} current={node.task_id} busy={busy} error={error}
                      onCancel={() => setChangingTask(false)}
                      onSave={(t) => act(() => api.put(`/content/nodes/${node.resource_id}/task`, { task_id: t }))} />)}
      </section>
      <section className="pp-panel">
        <SectionHead title="Rubrics" addLabel="Score this lesson on another rubric" adding={addingRubric}
                     onAdd={() => setAddingRubric(!addingRubric)} disabledWhy={why} />
        {addingRubric && (
          <RubricPicker rubrics={notHeld} busy={busy} error={error} onCancel={() => setAddingRubric(false)}
                        onSave={(v) => setRubrics([...node.rubric_version_ids, v])} />)}
        {held.length === 0 ? <p className="sc-mut">None yet.</p> : (
          <table className="tbl">
            <thead><tr><th>Rubric</th><th>Version</th><th /></tr></thead>
            <tbody>
              {held.map((h) => (
                <LessonRubricRow key={h.rubric_version_id} held={h} all={versions} mayEdit={mayEdit} busy={busy} why={why}
                                 onChange={(v) => setRubrics(node.rubric_version_ids.map((x) => (x === h.rubric_version_id ? v : x)))}
                                 onRemove={() => setRubrics(node.rubric_version_ids.filter((x) => x !== h.rubric_version_id))} />))}
            </tbody>
          </table>)}
      </section>
      <section className="pp-panel">
        <label className="pp-check">
          <input type="checkbox" checked={node.rigorous} disabled={!mayEdit || busy}
                 onChange={(e) => act(() => api.put(`/content/nodes/${node.resource_id}/rigorous`,
                                                    { rigorous: e.target.checked }))} />
          {" "}Counts toward measurement
        </label>
        <p className="sc-mut">Its scores enter the MFRM and the record.</p>
        {error && !changingTask && !addingRubric && <p className="inc-error">{error}</p>}
      </section>
    </>
  );
}

interface RubricVersionRef { rubric_version_id: string; rubric_id: string; name: string; version: number }

/** Every published version of these rubrics, newest first: what a lesson can name. */
function useRubricVersions(rubrics: RubricRow[]): RubricVersionRef[] {
  const [out, setOut] = useState<RubricVersionRef[]>([]);
  const ids = rubrics.filter((r) => r.published).map((r) => r.rubric_id).join(",");
  useEffect(() => {
    let live = true;
    Promise.all(ids.split(",").filter(Boolean).map((id) => api.get<RubricView>(`/content/rubrics/${encodeURIComponent(id)}`)))
      .then((views) => {
        if (live) setOut(views.flatMap((r) => r.versions.filter((v) => v.status === "published")
          .map((v) => ({ rubric_version_id: v.rubric_version_id, rubric_id: r.rubric_id, name: r.name, version: v.version }))));
      }, () => undefined);
    return () => { live = false; };
  }, [ids]);
  return out;
}

function LessonRubricRow({ held, all, mayEdit, busy, why, onChange, onRemove }: {
  held: RubricVersionRef; all: RubricVersionRef[]; mayEdit: boolean; busy: boolean; why: string | null;
  onChange: (v: string) => void; onRemove: () => void }) {
  const [changing, setChanging] = useState(false);
  const [chosen, setChosen] = useState(held.rubric_version_id);
  const mine = all.filter((v) => v.rubric_id === held.rubric_id);
  return (
    <tr>
      <td>{held.name}</td>
      <td>{changing
        ? <span className="ct-inline">
            <select aria-label={`${held.name} version`} value={chosen} onChange={(e) => setChosen(e.target.value)}>
              {mine.map((v) => <option key={v.rubric_version_id} value={v.rubric_version_id}>{v.version}</option>)}
            </select>
            <button type="button" className="btn primary sm" disabled={busy || chosen === held.rubric_version_id}
                    onClick={() => { onChange(chosen); setChanging(false); }}>Save</button>
            <button type="button" className="btn sm" onClick={() => { setChosen(held.rubric_version_id); setChanging(false); }}>Cancel</button>
          </span>
        : held.version || ""}</td>
      <td className="r">
        <IconButton icon="pencil" size="row" label={`Change the version of ${held.name}`} pressed={changing}
                    disabled={!mayEdit || busy} why={why} onClick={() => setChanging(!changing)} />
        <IconButton icon="x" size="row" danger label={`Stop scoring on ${held.name}`}
                    disabled={!mayEdit || busy} why={why} onClick={onRemove} />
      </td>
    </tr>
  );
}

function TaskPicker({ tasks, current, busy, error, onSave, onCancel }: {
  tasks: TaskRow[]; current: string | null; busy: boolean; error: string | null;
  onSave: (taskId: string | null) => void; onCancel: () => void }) {
  const [taskId, setTaskId] = useState(current ?? "");
  return (
    <AddForm saveLabel="Save" busy={busy} error={error} onCancel={onCancel}
             missing={taskId === (current ?? "") ? "Nothing has changed." : null} onSave={() => onSave(taskId || null)}>
      <label>Task
        <select value={taskId} autoFocus onChange={(e) => setTaskId(e.target.value)}>
          <option value="">None: not scored</option>
          {tasks.map((t) => <option key={t.task_id} value={t.task_id}>{t.name}</option>)}
        </select></label>
    </AddForm>
  );
}

function RubricPicker({ rubrics, busy, error, onSave, onCancel }: {
  rubrics: RubricRow[]; busy: boolean; error: string | null; onSave: (v: string) => void; onCancel: () => void }) {
  const [rubricId, setRubricId] = useState("");
  const view = useGet<RubricView>(rubricId ? `/content/rubrics/${encodeURIComponent(rubricId)}` : null);
  const versions = (view.data?.rubric_id === rubricId ? view.data.versions : []).filter((v) => v.status === "published");
  const [version, setVersion] = useState("");
  const chosen = versions.some((v) => v.rubric_version_id === version) ? version : versions[0]?.rubric_version_id ?? "";
  return (
    <AddForm saveLabel="Add it" busy={busy} error={error} onCancel={onCancel} missing={chosen ? null : "Choose a rubric."}
             onSave={() => onSave(chosen)}>
      <label>Rubric
        <select value={rubricId} autoFocus onChange={(e) => { setRubricId(e.target.value); setVersion(""); }}>
          <option value="">Choose…</option>
          {rubrics.map((r) => <option key={r.rubric_id} value={r.rubric_id}>{r.name}</option>)}
        </select></label>
      {rubricId && (
        <label>Version
          <select value={chosen} onChange={(e) => setVersion(e.target.value)}>
            {versions.map((v) => <option key={v.rubric_version_id} value={v.rubric_version_id}>{v.version}</option>)}
          </select></label>)}
    </AddForm>
  );
}

// -------------------------------------------------------------------------------- publish

function Publish({ id, view, onChanged }: { id: string; view: CurriculumView; onChanged: () => void }) {
  const counts = changeCounts(view.curriculum, view.removed);
  const first = !view.curriculum.shows;
  const [level, setLevel] = useState<"minor" | "major">(view.may_be_a_fix ? "minor" : "major");
  const [tag, setTag] = useState("");
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const { busy, error, act } = useAct(onChanged);
  const nothing = !first && counts.new + counts.changed + counts.removed === 0
    && view.curriculum.state === "kept";
  const blocked = view.problems.length > 0 || nothing;
  async function publish() {
    let made: string | null = null;
    if (await act(async () => {
      made = (await api.post<{ version: string }>(`/content/curricula/${id}/publish`,
        { level: first ? "major" : level, name_tag: tag, notes })).version;
    })) { setDone(made); setTag(""); setNotes(""); }
  }
  return (
    <section className="pp-panel">
      <h4>Since {first ? "it was started" : `version ${view.curriculum.shows}`}</h4>
      <p>{counts.new} new · {counts.changed} changed · {counts.removed} removed · {counts.kept} unchanged</p>
      {view.problems.length > 0 && (
        <div><p className="inc-error">Not yet:</p><ul>{view.problems.map((p) => <li key={p}>{p}</li>)}</ul></div>)}
      {nothing && <p className="sc-mut">Nothing has changed since the last version.</p>}
      {first ? <p>The first publication is version 1.0.</p> : (
        <fieldset className="ct-level">
          <label className="pp-check">
            <input type="radio" name="level" checked={level === "minor"} disabled={!view.may_be_a_fix}
                   onChange={() => setLevel("minor")} />{" "}
            A fix: every class following this edition gets it at once
            {!view.may_be_a_fix && <span className="sc-mut"> (not possible: something was added, removed or moved)</span>}
          </label>
          <label className="pp-check">
            <input type="radio" name="level" checked={level === "major"} onChange={() => setLevel("major")} />{" "}
            A new edition: licensed on its own; classes on the old one keep it
          </label>
        </fieldset>)}
      <div className="pp-form">
        <label>Name tag (optional)<input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="2026–27 edition" /></label>
        <label className="ct-wide">Notes<textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
      </div>
      <div className="pp-actions">
        <button type="button" className="btn primary sm" disabled={busy || blocked || !view.may_publish}
                title={view.may_publish ? undefined : "Only the provider's configurators publish."}
                onClick={publish}>Publish</button>
      </div>
      {done && <p className="sc-note">Published version {done}.</p>}
      {error && <p className="inc-error">{error}</p>}
    </section>
  );
}

// ------------------------------------------------------------------------------- versions

function Versions({ id, mayPublish, reloadKey }: { id: string; mayPublish: boolean; reloadKey: number }) {
  const [key, setKey] = useState(0);
  const load = useGet<{ versions: Version[] }>(`/content/curricula/${id}/versions`, key + reloadKey);
  const [before, setBefore] = useState("");
  const [after, setAfter] = useState("");
  const diff = useGet<{ before: string; after: string; changes: Change[] }>(
    before && after && before !== after
      ? `/content/compare?before=${encodeURIComponent(before)}&after=${encodeURIComponent(after)}` : null);
  const [withdrawing, setWithdrawing] = useState<Version | null>(null);
  const { busy, error, act } = useAct(() => setKey((k) => k + 1));
  const versions = load.data?.versions ?? [];
  return (
    <section className="pp-panel">
      {notReady(load, "versions") ?? (versions.length === 0 ? <p className="sc-mut">Not published yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Version</th><th>Published</th><th>Notes</th><th></th></tr></thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.version_id} className={v.withdrawn_at ? "pp-disabled" : undefined}>
                <td>{v.version}{v.name_tag && <span className="sc-chip">{v.name_tag}</span>}
                    {v.withdrawn_at && <span className="sc-chip">withdrawn {day(v.withdrawn_at)}</span>}</td>
                <td>{day(v.published_at)}</td>
                <td>{v.notes ?? ""}</td>
                <td className="r">{!v.withdrawn_at && (
                  <IconButton icon="trash" size="row" danger label={`Withdraw ${v.version}`}
                              disabled={!mayPublish || busy} why="Only the provider's configurators withdraw."
                              onClick={() => setWithdrawing(v)} />)}</td>
              </tr>))}
          </tbody>
        </table>))}
      {versions.length > 1 && (
        <>
          <h4>Compare</h4>
          <div className="pp-form">
            {([["From", before, setBefore], ["To", after, setAfter]] as const).map(([label, v, set]) => (
              <label key={label}>{label}
                <select value={v} onChange={(e) => set(e.target.value)}>
                  <option value="">—</option>
                  {versions.map((x) => <option key={x.version_id} value={x.version_id}>{x.version}</option>)}
                </select></label>))}
          </div>
          {before && after && before !== after && !diff.data && notReady(diff, "the comparison")}
          {diff.data && (
            <table className="tbl">
              <thead><tr><th>Unit or lesson</th><th>{diff.data.before} → {diff.data.after}</th></tr></thead>
              <tbody>
                {diff.data.changes.map((c) => (
                  <tr key={c.resource_id} className={c.state === "kept" ? "pp-disabled" : undefined}>
                    <td className={c.kind === "lesson" ? "ct-indent" : undefined}>{c.name}</td>
                    <td>{c.state}</td>
                  </tr>))}
              </tbody>
            </table>)}
        </>)}
      {error && <p className="inc-error">{error}</p>}
      <ConfirmDialog open={!!withdrawing} title={`Withdraw ${withdrawing?.version}?`} confirmLabel="Withdraw"
                     busy={busy} onCancel={() => setWithdrawing(null)}
                     onConfirm={() => act(() => api.post(`/content/versions/${withdrawing!.version_id}/withdraw`, {}))
                       .then(() => setWithdrawing(null))}>
        <p>A withdrawn fix stops being shown: what pointed at it shows the version before. A withdrawn
           edition takes no new classes; those already on it keep it.</p>
      </ConfirmDialog>
    </section>
  );
}

// ------------------------------------------------------------------------------- licenses

function Licenses({ id, mayPublish }: { id: string; mayPublish: boolean }) {
  const [key, setKey] = useState(0);
  const versions = useGet<{ versions: Version[] }>(`/content/curricula/${id}/versions`);
  const load = useGet<{ licenses: License[] }>(`/content/curricula/${id}/licenses`, key);
  const districts = useGet<{ districts: { district_id: string; name: string }[] }>(mayPublish ? "/content/districts" : null);
  const { busy, error, act } = useAct(() => setKey((k) => k + 1));
  const [adding, setAdding] = useState<string | null>(null);
  const [ending, setEnding] = useState<{ edition_id: string; district: string; name: string } | null>(null);
  const [form, setForm] = useState({ district_id: "", access: true, scoring: false, from: "", until: "" });
  const editions = editionsOf(versions.data?.versions ?? []);
  const why = mayPublish ? null : "Only the provider's configurators license and give away.";
  if (versions.data && editions.length === 0) return <p className="sc-mut">Publish it first: a license is for an edition.</p>;
  return (
    <section>
      {notReady(load, "licenses")}
      {editions.map((e) => {
        const rows = (load.data?.licenses ?? []).filter((l) => l.edition_id === e.edition_id);
        const free = rows.some((l) => l.relation === "reader");
        const freeScoring = rows.some((l) => l.relation === "scorer" && l.district_id === "*");
        const byDistrict = new Map<string, License[]>();
        rows.filter((l) => l.district_id !== "*").forEach((l) =>
          byDistrict.set(l.district_id, [...(byDistrict.get(l.district_id) ?? []), l]));
        return (
          <div key={e.edition_id} className="pp-panel">
            <SectionHead title={`Edition ${e.major}`} addLabel={`License edition ${e.major} to a district`}
                         adding={adding === e.edition_id} disabledWhy={why}
                         onAdd={() => setAdding(adding === e.edition_id ? null : e.edition_id)} />
            <label className="pp-check">
              <input type="checkbox" checked={free} disabled={!mayPublish || busy}
                     onChange={(ev) => act(() => api.put("/content/free",
                       { type: "edition", id: e.edition_id, free: ev.target.checked }))} />
              {" "}Free for everyone signed in{freeScoring ? ", with scoring" : ""}
            </label>
            {adding === e.edition_id && (
              <AddForm saveLabel="License it" busy={busy} error={error} onCancel={() => setAdding(null)}
                       missing={!form.district_id ? "Choose a district." : !(form.access || form.scoring) ? "Choose what it gives: Access, Scoring included, or both." : null}
                       note="When the term ends, classes finish their school year on it and the district keeps read-only what it had."
                       onSave={() => act(() => api.post("/content/licenses", { edition_id: e.edition_id, ...form,
                                                                                 from: form.from || null, until: form.until || null }))
                         .then((ok) => { if (ok) setAdding(null); })}>
                <label>District
                  <select value={form.district_id} onChange={(ev) => setForm({ ...form, district_id: ev.target.value })}>
                    <option value="">Choose…</option>
                    {(districts.data?.districts ?? []).map((d) => <option key={d.district_id} value={d.district_id}>{d.name}</option>)}
                  </select></label>
                <label className="pp-check"><input type="checkbox" checked={form.access}
                       onChange={(ev) => setForm({ ...form, access: ev.target.checked })} /> Access</label>
                <label className="pp-check"><input type="checkbox" checked={form.scoring}
                       onChange={(ev) => setForm({ ...form, scoring: ev.target.checked })} /> Scoring included</label>
                <label>From<input type="date" value={form.from} onChange={(ev) => setForm({ ...form, from: ev.target.value })} /></label>
                <label>Until<input type="date" value={form.until} onChange={(ev) => setForm({ ...form, until: ev.target.value })} /></label>
              </AddForm>)}
            {byDistrict.size === 0 ? <p className="sc-mut">Licensed to no district.</p> : (
              <table className="tbl">
                <thead><tr><th>District</th><th>Holds</th><th></th></tr></thead>
                <tbody>
                  {[...byDistrict].map(([district, ls]) => (
                    <tr key={district}>
                      <td>{ls[0].district ?? district}</td>
                      <td>{ls.map((l) => `${l.relation === "licensee" ? "access" : l.relation === "scorer" ? "scoring"
                                           : "read-only, kept"} from ${day(l.valid_from)}${l.valid_until ? ` until ${day(l.valid_until)}` : ""}`).join("; ")}</td>
                      <td className="r">{ls.some((l) => l.relation !== "keeper") && (
                        <IconButton icon="trash" size="row" danger
                                    label={`End ${ls[0].district ?? district}'s license now`}
                                    disabled={!mayPublish || busy} why={why}
                                    onClick={() => setEnding({ edition_id: e.edition_id, district,
                                                               name: ls[0].district ?? district })} />)}</td>
                    </tr>))}
                </tbody>
              </table>)}
          </div>);
      })}
      {error && !adding && <p className="inc-error">{error}</p>}
      <ConfirmDialog open={!!ending} title={`End ${ending?.name}'s license now?`} confirmLabel="End it"
                     busy={busy} onCancel={() => setEnding(null)}
                     onConfirm={() => act(() => api.post("/content/licenses/end",
                       { edition_id: ending!.edition_id, district_id: ending!.district }))
                       .then(() => setEnding(null))}>
        <p>{ending?.name}'s people stop using this edition today, and its scoring stops. They keep,
           read only, what was published until today.</p>
      </ConfirmDialog>
    </section>
  );
}

// -------------------------------------------------------------------------------- writers

function Writers({ id, mayPublish }: { id: string; mayPublish: boolean }) {
  const [key, setKey] = useState(0);
  const load = useGet<{ writers: Writer[] }>(`/content/curricula/${id}/writers`, key);
  const people = useGet<{ people: { subject_id: string; name: string | null; email: string | null }[] }>(
    mayPublish ? "/people" : null);
  const [adding, setAdding] = useState(false);
  const [who, setWho] = useState("");
  const { busy, error, act } = useAct(() => setKey((k) => k + 1));
  const why = mayPublish ? null : "Only the provider's configurators name its writers.";
  return (
    <section className="pp-panel">
      <SectionHead title="Writers" addLabel="Name a writer" adding={adding} disabledWhy={why}
                   onAdd={() => setAdding(!adding)} />
      <p className="sc-mut">Writers change the draft; the provider's configurators also publish.</p>
      {adding && (
        <AddForm saveLabel="Name them" missing={who ? null : "Choose a person."} busy={busy} error={error} onCancel={() => setAdding(false)}
                 onSave={() => act(() => api.post(`/content/curricula/${id}/writers`, { subject_id: who }))
                   .then((ok) => { if (ok) { setAdding(false); setWho(""); } })}
                 note={people.error ? "The people list is not open to you here; ask Preva." : undefined}>
          <label>Person
            <select value={who} onChange={(e) => setWho(e.target.value)}>
              <option value="">Choose…</option>
              {(people.data?.people ?? []).map((p) => (
                <option key={p.subject_id} value={p.subject_id}>{p.name ?? p.email ?? p.subject_id}</option>))}
            </select></label>
        </AddForm>)}
      {notReady(load, "writers") ?? (load.data!.writers.length === 0 ? <p className="sc-mut">No named writers.</p> : (
        <ul className="pp-list">
          {load.data!.writers.map((w) => (
            <li key={w.relationship_id} className="pp-role">
              <span>{w.name}{w.subject_type === "team" && <span className="sc-chip">team</span>}</span>
              <IconButton icon="x" size="item" danger label={`Stop ${w.name} writing`} disabled={!mayPublish || busy}
                          why={why} onClick={() => act(() => api.del(`/content/writers/${w.relationship_id}`))} />
            </li>))}
        </ul>))}
      {error && !adding && <p className="inc-error">{error}</p>}
    </section>
  );
}

// ================================================================================ Rubrics
// A list of rubrics, each opening into its traits, each held at a major (docs/DESIGN.md, "A list
// of things", "Naming a version"). A trait's own wording is on Traits.

export function ContentRubrics() {
  return <Page title="Rubrics" addLabel="Make a rubric">{(p, add) => <RubricList provider={p} add={add} />}</Page>;
}

function RubricList({ provider, add }: { provider: Provider; add: Adding }) {
  const [key, setKey] = useState(0);
  const reload = () => setKey((k) => k + 1);
  const load = useGet<{ rubrics: RubricRow[] }>(`/content/rubrics?provider_id=${encodeURIComponent(provider.provider_id)}`, key);
  const [open, choose] = useQueryParam("rubric");
  const { adding, setAdding } = add;
  const [form, setForm] = useState({ name: "", grade_band: "" });
  const { busy, error, act } = useAct(reload);
  return (
    <section>
      {adding && (
        <AddForm saveLabel="Make it" busy={busy} error={error} onCancel={() => setAdding(false)}
                 missing={!form.name.trim() ? "Enter a name." : !form.grade_band.trim() ? "Enter the grades." : null}
                 onSave={async () => {
                   let made: string | null = null;
                   if (await act(async () => {
                     made = (await api.post<{ rubric_id: string }>("/content/rubrics",
                       { provider_id: provider.provider_id, ...form })).rubric_id;
                   })) { setAdding(false); setForm({ name: "", grade_band: "" }); if (made) choose(made); }
                 }}>
          <label>Name<input value={form.name} autoFocus onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label>Grades<input value={form.grade_band} placeholder="11-12"
                              onChange={(e) => setForm({ ...form, grade_band: e.target.value })} /></label>
        </AddForm>)}
      {notReady(load, "rubrics") ?? (load.data!.rubrics.length === 0 ? <p className="sc-mut">None yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Rubric</th><th>Grades</th><th>Version</th><th /></tr></thead>
          <tbody>
            {load.data!.rubrics.map((r) => [
              <tr key={r.rubric_id}>
                <td>{r.name}</td>
                <td>{r.grade_band ?? ""}</td>
                <td>{r.published ?? <span className="sc-mut">not published</span>}
                  {r.draft_id && <span className="sc-chip ct-changed">draft</span>}</td>
                <td className="r">
                  <button type="button" className="btn-link" aria-expanded={open === r.rubric_id}
                          onClick={() => choose(open === r.rubric_id ? null : r.rubric_id)}>
                    {open === r.rubric_id ? "Hide" : "Open"}</button></td>
              </tr>,
              open === r.rubric_id && (
                <tr key={`${r.rubric_id}:panel`} className="pp-add">
                  <td colSpan={4}><RubricPanel id={r.rubric_id} provider={provider} onChanged={reload} /></td>
                </tr>)])}
          </tbody>
        </table>))}
    </section>
  );
}

function RubricPanel({ id, provider, onChanged }: { id: string; provider: Provider; onChanged: () => void }) {
  const [key, setKey] = useState(0);
  const reload = () => { setKey((k) => k + 1); onChanged(); };
  const load = useGet<RubricView>(`/content/rubrics/${encodeURIComponent(id)}`, key);
  const [chosen, setChosen] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const { busy, error, act } = useAct(reload);
  if (!load.data) return <>{notReady(load, "the rubric")}</>;
  const r = load.data;
  const draft = r.versions.find((v) => v.status === "draft") ?? null;
  const published = r.versions.filter((v) => v.status !== "draft");
  const shown = published.find((v) => v.rubric_version_id === chosen) ?? published[0] ?? null;
  async function edit() {
    if (draft || await act(() => api.post(`/content/rubrics/${id}/draft`, {}))) setEditing(true);
  }
  if ((editing || !shown) && draft)
    return <RubricDraft draft={draft} provider={provider} onChanged={reload} onDone={() => setEditing(false)} />;
  return (
    <div>
      <div className="ct-inline">
        {published.length > 0 && (
          <label>Version{" "}
            <select value={shown?.rubric_version_id ?? ""} onChange={(e) => setChosen(e.target.value)}>
              {published.map((v) => <option key={v.rubric_version_id} value={v.rubric_version_id}>
                {v.version}{v.name_tag ? ` · ${v.name_tag}` : ""}</option>)}
            </select></label>)}
        {shown?.notes && <span className="sc-mut">{shown.notes}</span>}
        <span className="ct-push" />
        <IconButton icon="pencil" size="row" label={`Change ${r.name}`} disabled={busy} onClick={edit} />
      </div>
      {error && <p className="inc-error">{error}</p>}
      {shown && <RubricTraits traits={shown.traits} />}
    </div>
  );
}

function RubricTraits({ traits }: { traits: RubricTrait[] }) {
  if (traits.length === 0) return <p className="sc-mut">No traits.</p>;
  return (
    <table className="tbl">
      <thead><tr><th>Trait</th><th>Version</th><th>Levels</th></tr></thead>
      <tbody>
        {traits.map((t) => (
          <tr key={t.trait_id}>
            <td><a href={`?trait=${encodeURIComponent(t.trait_id)}`} onClick={(e) => { e.preventDefault(); openTrait(t.trait_id); }}>
              {t.trait_label}</a></td>
            <td>{t.trait_major}</td>
            <td>{levelRange(t.scale_categories)}</td>
          </tr>))}
      </tbody>
    </table>
  );
}

/** Go to a trait on Traits. */
function openTrait(traitId: string) {
  const url = new URL(window.location.href);
  url.pathname = url.pathname.replace(/\/rubrics\/?$/, "/traits");
  url.search = `?trait=${encodeURIComponent(traitId)}`;
  window.history.pushState({}, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function RubricDraft({ draft, provider, onChanged, onDone }: {
  draft: RubricVersion; provider: Provider; onChanged: () => void; onDone: () => void }) {
  const traits = useGet<{ traits: TraitRow[] }>(`/content/traits?provider_id=${encodeURIComponent(provider.provider_id)}`);
  const problems = useGet<{ problems: string[] }>(`/content/rubric-versions/${draft.rubric_version_id}/problems`,
                                                  JSON.stringify(draft.traits).length);
  const { busy, error, act } = useAct(onChanged);
  const [adding, setAdding] = useState(false);
  const [existing, setExisting] = useState("");
  const base = `/content/rubric-versions/${draft.rubric_version_id}`;
  const held = draft.traits.map((t) => ({ trait_id: t.trait_id, major: t.trait_major }));
  const ids = held.map((t) => t.trait_id);
  const setTraits = (next: { trait_id: string; major?: number }[]) => act(() => api.put(`${base}/traits`, { traits: next }));
  function move(id: string, dir: -1 | 1) {
    const to = movedTo(ids, id, dir);
    if (!to) return;
    const next = held.filter((x) => x.trait_id !== id);
    next.splice(to - 1, 0, held.find((x) => x.trait_id === id)!);
    setTraits(next);
  }
  const others = (traits.data?.traits ?? []).filter((t) => !ids.includes(t.trait_id));
  const blocked = !problems.data || problems.data.problems.length > 0;
  return (
    <div>
      <SectionHead title="Traits" addLabel="Add a trait" adding={adding} onAdd={() => setAdding(!adding)} />
      {adding && (
        <AddForm saveLabel="Add it" busy={busy} error={error} onCancel={() => setAdding(false)} missing={existing ? null : "Choose a trait."}
                 note={others.length === 0 ? "Every trait of yours is in it. Make a new one on Traits." : undefined}
                 onSave={() => setTraits([...held, { trait_id: existing }]).then((ok) => { if (ok) { setAdding(false); setExisting(""); } })}>
          <label>Trait
            <select value={existing} autoFocus onChange={(e) => setExisting(e.target.value)}>
              <option value="">Choose…</option>
              {others.map((t) => <option key={t.trait_id} value={t.trait_id}>{t.trait_label}</option>)}
            </select></label>
        </AddForm>)}
      {draft.traits.length === 0 ? <p className="sc-mut">None yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Trait</th><th>Version</th><th>Levels</th><th /></tr></thead>
          <tbody>
            {draft.traits.map((t) => {
              const row = (traits.data?.traits ?? []).find((x) => x.trait_id === t.trait_id);
              const majors = majorsOf(row?.version ?? null, t.trait_major);
              return (
                <tr key={t.trait_id}>
                  <td>{t.trait_label}</td>
                  <td>
                    <select aria-label={`${t.trait_label} version`} value={t.trait_major} disabled={busy}
                            onChange={(e) => setTraits(held.map((x) => (x.trait_id === t.trait_id ? { ...x, major: Number(e.target.value) } : x)))}>
                      {majors.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select></td>
                  <td>{levelRange(t.scale_categories)}</td>
                  <td className="r ct-actions">
                    <button type="button" className="btn sm" aria-label={`Move ${t.trait_label} up`}
                            disabled={busy || movedTo(ids, t.trait_id, -1) === null} onClick={() => move(t.trait_id, -1)}>↑</button>
                    <button type="button" className="btn sm" aria-label={`Move ${t.trait_label} down`}
                            disabled={busy || movedTo(ids, t.trait_id, 1) === null} onClick={() => move(t.trait_id, 1)}>↓</button>
                    <IconButton icon="x" size="row" danger label={`Take ${t.trait_label} out`} disabled={busy}
                                onClick={() => setTraits(held.filter((x) => x.trait_id !== t.trait_id))} />
                  </td>
                </tr>);
            })}
          </tbody>
        </table>)}
      {problems.data && problems.data.problems.length > 0 && (
        <ul className="inc-error">{problems.data.problems.map((p) => <li key={p}>{p}</li>)}</ul>)}
      <div className="pp-actions">
        <button type="button" className="btn primary sm" title={provider.configures ? undefined : "Only the provider's configurators publish."}
                disabled={busy || !provider.configures || blocked}
                onClick={() => act(() => api.post(`${base}/publish`, {})).then((ok) => { if (ok) onDone(); })}>
          Publish {draft.version}</button>
        <button type="button" className="btn sm" disabled={busy} onClick={onDone}>Close</button>
      </div>
      {error && !adding && <p className="inc-error">{error}</p>}
    </div>
  );
}

/** The majors a rubric may hold a trait at: 1 up to its newest, and the one it holds now. */
export function majorsOf(newest: string | null, held: number): number[] {
  const top = Math.max(newest ? Number(newest.split(".")[0]) : 1, held);
  return Array.from({ length: top }, (_, i) => top - i);
}

/** "1–4" for a scale of 1, 2, 3, 4. */
export function levelRange(scale: (number | string)[]): string {
  return scale.length === 0 ? "" : `${scale[0]}–${scale[scale.length - 1]}`;
}

// ================================================================================== Traits
// A list of traits, each opening into its levels: the descriptor and the augmented descriptor of
// each, at a version (docs/DESIGN.md, "Versions"). The pencil turns the same table into fields.

export function ContentTraits() {
  return <Page title="Traits" addLabel="Make a trait">{(p, add) => <TraitList provider={p} add={add} />}</Page>;
}

function TraitList({ provider, add }: { provider: Provider; add: Adding }) {
  const [key, setKey] = useState(0);
  const reload = () => setKey((k) => k + 1);
  const load = useGet<{ traits: TraitRow[] }>(`/content/traits?provider_id=${encodeURIComponent(provider.provider_id)}`, key);
  const [open, choose] = useQueryParam("trait");
  const { adding, setAdding } = add;
  const [form, setForm] = useState({ name: "", standard: "", grade_band: "", levels: "1, 2, 3, 4" });
  const { busy, error, act } = useAct(reload);
  return (
    <section>
      {adding && (
        <AddForm saveLabel="Make it" busy={busy} error={error} onCancel={() => setAdding(false)}
                 missing={!form.name.trim() ? "Enter a name." : !form.standard.trim() ? "Enter the standard."
                   : !form.grade_band.trim() ? "Enter the grades." : levelsFrom(form.levels).length < 2 ? "Give it at least two levels." : null}
                 note="Its levels are what it is: a different scale is a different trait."
                 onSave={async () => {
                   let made: string | null = null;
                   if (await act(async () => {
                     made = (await api.post<{ trait_id: string }>("/content/traits", {
                       provider_id: provider.provider_id, name: form.name, standard: form.standard,
                       grade_band: form.grade_band, scale: levelsFrom(form.levels) })).trait_id;
                   })) { setAdding(false); setForm({ name: "", standard: "", grade_band: "", levels: "1, 2, 3, 4" }); if (made) choose(made); }
                 }}>
          <label>Name<input value={form.name} autoFocus onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label>Standard<input value={form.standard} placeholder="W.9-10.1a" onChange={(e) => setForm({ ...form, standard: e.target.value })} /></label>
          <label>Grades<input value={form.grade_band} placeholder="9-10" onChange={(e) => setForm({ ...form, grade_band: e.target.value })} /></label>
          <label>Levels<input value={form.levels} onChange={(e) => setForm({ ...form, levels: e.target.value })} /></label>
        </AddForm>)}
      {notReady(load, "traits") ?? (load.data!.traits.length === 0 ? <p className="sc-mut">None yet.</p> : (
        <table className="tbl">
          <thead><tr><th>Trait</th><th>Standard</th><th>Grades</th><th>Version</th><th>Used in</th><th /></tr></thead>
          <tbody>
            {load.data!.traits.map((t) => [
              <tr key={t.trait_id}>
                <td>{t.trait_label}</td>
                <td><code>{t.learning_standard_item}</code></td>
                <td>{t.grade_band}</td>
                <td>{t.version ?? (t.has_draft ? "Draft" : "")}</td>
                <td><UsedIn trait={t} /></td>
                <td className="r">
                  <button type="button" className="btn-link" aria-expanded={open === t.trait_id}
                          onClick={() => choose(open === t.trait_id ? null : t.trait_id)}>
                    {open === t.trait_id ? "Hide" : "Open"}</button></td>
              </tr>,
              open === t.trait_id && (
                <tr key={`${t.trait_id}:panel`} className="pp-add">
                  <td colSpan={6}><TraitPanel id={t.trait_id} provider={provider} onChanged={reload} /></td>
                </tr>)])}
          </tbody>
        </table>))}
    </section>
  );
}

/** "3 rubrics", which opens the list right under it (docs/DESIGN.md, "A count opens what it
 *  counts"). */
function UsedIn({ trait }: { trait: TraitRow }) {
  const [open, setOpen] = useState(false);
  const load = useGet<TraitView>(open ? `/content/traits/${encodeURIComponent(trait.trait_id)}` : null);
  if (trait.rubrics === 0) return <span className="sc-mut">None yet</span>;
  return (
    <>
      <button type="button" className="btn-link" aria-expanded={open} onClick={() => setOpen(!open)}>
        {trait.rubrics} rubric{trait.rubrics === 1 ? "" : "s"}</button>
      {open && (notReady(load, "rubrics") ?? (
        <table className="tbl ct-count">
          <thead><tr><th>Rubric</th><th>Rubric version</th><th>Version</th></tr></thead>
          <tbody>
            {load.data!.rubrics.filter((r) => r.status === "published").map((r) => (
              <tr key={r.rubric_version_id}><td>{r.name}</td><td>{r.version}</td><td>{r.trait_major}</td></tr>))}
          </tbody>
        </table>))}
    </>
  );
}

function TraitPanel({ id, provider, onChanged }: { id: string; provider: Provider; onChanged: () => void }) {
  const [key, setKey] = useState(0);
  const reload = () => { setKey((k) => k + 1); onChanged(); };
  const load = useGet<TraitView>(`/content/traits/${encodeURIComponent(id)}`, key);
  const [chosen, setChosen] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const { busy, error, act } = useAct(reload);
  if (!load.data) return <>{notReady(load, "the trait")}</>;
  const t = load.data;
  const draft = t.versions.find((v) => v.status === "draft") ?? null;
  const published = t.versions.filter((v) => v.status !== "draft");
  const shown = published.find((v) => v.trait_version_id === chosen) ?? published[0] ?? null;
  async function edit() {
    if (draft || await act(() => api.post(`/content/traits/${id}/draft`, { from_version_id: shown?.trait_version_id }))) setEditing(true);
  }
  if ((editing || !shown) && draft)
    return <TraitDraft trait={t} draft={draft} provider={provider} onChanged={reload} onDone={() => setEditing(false)} />;
  if (!shown) return <p className="sc-mut">No wording yet.</p>;
  return (
    <div>
      <div className="ct-inline">
        <label>Version{" "}
          <select value={shown.trait_version_id} onChange={(e) => setChosen(e.target.value)}>
            {published.map((v) => <option key={v.trait_version_id} value={v.trait_version_id}>{v.version}</option>)}
          </select></label>
        {shown.change_note && <span className="sc-mut">{shown.change_note}</span>}
        <span className="ct-push" />
        {draft && <span className="sc-chip ct-changed">draft {draft.version}</span>}
        <IconButton icon="pencil" size="row" label={`Change ${t.trait_label}`} disabled={busy} onClick={edit} />
      </div>
      {error && <p className="inc-error">{error}</p>}
      <table className="tbl">
        <thead><tr><th>Level</th><th>Descriptor</th><th>Augmented descriptor</th></tr></thead>
        <tbody>
          {t.scale_categories.map((c) => (
            <tr key={String(c)}>
              <td>{c}</td>
              <td><Clauses value={shown.descriptors[String(c)]} /></td>
              <td>{shown.augmented.levels?.[String(c)] ?? ""}</td>
            </tr>))}
        </tbody>
      </table>
      {shown.augmented.general && <p className="sc-mut">{shown.augmented.general}</p>}
    </div>
  );
}

function Clauses({ value }: { value: Descriptor | undefined }) {
  const clauses = clausesOf(value);
  if (clauses.length <= 1) return <>{clauses[0] ?? ""}</>;
  return <ul className="ct-clauses">{clauses.map((c, i) => <li key={i}>{c}</li>)}</ul>;
}

function TraitDraft({ trait, draft, provider, onChanged, onDone }: {
  trait: TraitView; draft: TraitVersion; provider: Provider; onChanged: () => void; onDone: () => void }) {
  const levels = trait.scale_categories.map(String);
  const [label, setLabel] = useState(draft.model_label);
  const [text, setText] = useState<Record<string, string>>(
    Object.fromEntries(levels.map((c) => [c, textOf(draft.descriptors[c])])));
  const [augmented, setAugmented] = useState<Record<string, string>>(
    Object.fromEntries(levels.map((c) => [c, draft.augmented.levels?.[c] ?? ""])));
  const [general, setGeneral] = useState(draft.augmented.general ?? "");
  const [note, setNote] = useState(draft.change_note ?? "");
  const [reason, setReason] = useState(draft.reason ?? "");
  const [discarding, setDiscarding] = useState(false);
  const { busy, error, act } = useAct(onChanged);
  const descriptors = Object.fromEntries(levels.map((c) => [c, descriptorFrom(text[c] ?? "")]));
  const number = numberFor(trait.versions, draft, label.trim(), descriptors);
  const major = number.endsWith(".0") && draft.based_on !== null;
  const body = {
    model_label: label, descriptors, change_note: note, reason: major ? reason : "",
    augmented: { ...(general.trim() ? { general: general.trim() } : {}),
                 levels: Object.fromEntries(levels.filter((c) => augmented[c]?.trim()).map((c) => [c, augmented[c].trim()])) },
  };
  const save = () => act(() => api.put(`/content/traits/${trait.trait_id}/draft`, body));
  async function publish() {
    if (await save()) {
      if (await act(() => api.post(`/content/traits/${trait.trait_id}/publish`, {}))) onDone();
    }
  }
  return (
    <div>
      <label className="pp-field">Label the model reads<input value={label} onChange={(e) => setLabel(e.target.value)} /></label>
      <table className="tbl ct-edit">
        <thead><tr><th>Level</th><th>Descriptor</th><th>Augmented descriptor</th></tr></thead>
        <tbody>
          {levels.map((c) => (
            <tr key={c}>
              <td>{c}</td>
              <td><textarea rows={3} aria-label={`Level ${c} descriptor`} value={text[c]}
                            onChange={(e) => setText({ ...text, [c]: e.target.value })} /></td>
              <td><textarea rows={3} aria-label={`Level ${c} augmented descriptor`} value={augmented[c]}
                            onChange={(e) => setAugmented({ ...augmented, [c]: e.target.value })} /></td>
            </tr>))}
        </tbody>
      </table>
      <label className="pp-field">General augmented descriptor
        <textarea rows={2} value={general} onChange={(e) => setGeneral(e.target.value)} /></label>
      <label className="pp-field">What changed<input value={note} onChange={(e) => setNote(e.target.value)} /></label>
      {major && (
        <label className="pp-field">Why it still measures the same thing
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></label>)}
      <div className="pp-actions">
        <button type="button" className="btn primary sm" disabled={busy || !provider.configures}
                title={provider.configures ? undefined : "Only the provider's configurators publish."}
                onClick={publish}>Publish {number}</button>
        <button type="button" className="btn sm" disabled={busy} onClick={() => save()}>Save draft</button>
        <button type="button" className="btn sm" disabled={busy} onClick={onDone}>Close</button>
        <span className="ct-push" />
        {draft.based_on && (
          <IconButton icon="trash" size="row" label="Discard the draft" disabled={busy} onClick={() => setDiscarding(true)} />)}
      </div>
      {error && <p className="inc-error">{error}</p>}
      <ConfirmDialog open={discarding} title="Discard the draft?" confirmLabel="Discard" busy={busy}
                     onCancel={() => setDiscarding(false)}
                     onConfirm={() => act(() => api.del(`/content/traits/${trait.trait_id}/draft`))
                       .then(() => { setDiscarding(false); onDone(); })}>
        <p>Its wording is gone. The published versions stay as they are.</p>
      </ConfirmDialog>
    </div>
  );
}

// ================================================================================== Tasks

export function ContentTasks() {
  const [open, choose] = useQueryParam("task");
  return (
    <Page title="Tasks">
      {(p) => <TasksTwo provider={p} open={open} choose={choose} />}
    </Page>
  );
}

function TasksTwo({ provider, open, choose }: { provider: Provider; open: string | null; choose: (id: string) => void }) {
  const [key, setKey] = useState(0);
  const load = useGet<{ tasks: TaskRow[] }>(`/content/tasks?provider_id=${encodeURIComponent(provider.provider_id)}`, key);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const { busy, error, act } = useAct(() => setKey((k) => k + 1));
  return (
    <div className="sc-two">
      <nav className="sc-tree" aria-label="Tasks">
        <SectionHead title="Tasks" addLabel="Write a task" adding={adding} onAdd={() => setAdding(!adding)} />
        {adding && (
          <AddForm saveLabel="Start it" missing={name.trim() ? null : "Enter a name."} busy={busy} error={error} onCancel={() => setAdding(false)}
                   onSave={async () => {
                     let made: string | null = null;
                     if (await act(async () => {
                       made = (await api.post<{ task_id: string }>("/content/tasks",
                         { provider_id: provider.provider_id, name })).task_id;
                     })) { setAdding(false); setName(""); if (made) choose(made); }
                   }}>
            <label>Name<input value={name} autoFocus onChange={(e) => setName(e.target.value)} /></label>
          </AddForm>)}
        {notReady(load, "tasks") ?? (load.data!.tasks.length === 0 ? <p className="sc-mut">None yet.</p> : (
          <ul>
            {load.data!.tasks.map((t) => (
              <li key={t.task_id}>
                <button type="button" className={"sc-trait" + (t.task_id === open ? " cur" : "")} onClick={() => choose(t.task_id)}>
                  {t.name}{!t.has_statement && <span className="sc-chip">no prompt</span>}
                </button>
              </li>))}
          </ul>))}
      </nav>
      <section className="sc-pane">
        {open ? <TaskPanel key={open} id={open} onChanged={() => setKey((k) => k + 1)} />
              : <p className="sc-mut">Choose a task on the left.</p>}
      </section>
    </div>
  );
}

function TaskPanel({ id, onChanged }: { id: string; onChanged: () => void }) {
  const [key, setKey] = useState(0);
  const reload = () => { setKey((k) => k + 1); onChanged(); };
  const load = useGet<TaskView>(`/content/tasks/${encodeURIComponent(id)}`, key);
  if (!load.data) return <>{notReady(load, "the task")}</>;
  return <TaskForm key={key} task={load.data} onChanged={reload} />;
}

function TaskForm({ task, onChanged }: { task: TaskView; onChanged: () => void }) {
  const [form, setForm] = useState({ name: task.name, statement: task.statement ?? "", grade_band: task.grade_band ?? "" });
  const [texts, setTexts] = useState<TaskText[]>(task.texts);
  const { busy, error, act } = useAct(onChanged);
  const base = `/content/tasks/${task.task_id}`;
  const textsChanged = JSON.stringify(texts) !== JSON.stringify(task.texts);
  const setText = (i: number, patch: Partial<TaskText>) => setTexts(texts.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  return (
    <section className="pp-panel">
      <div className="ct-fields">
        <label className="pp-field">Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="pp-field">Prompt
          <textarea rows={4} value={form.statement} onChange={(e) => setForm({ ...form, statement: e.target.value })} /></label>
        <label className="pp-field">Grades<input value={form.grade_band} onChange={(e) => setForm({ ...form, grade_band: e.target.value })} /></label>
        <div className="pp-actions">
          <button type="button" className="btn primary sm" disabled={busy || !form.name.trim()}
                  onClick={() => act(() => api.put(base, form))}>Save</button>
        </div>
      </div>
      <h4>Texts students read</h4>
      {texts.map((t, i) => (
        <div key={i} className="add-form ct-text">
          <label className="pp-field">Title<input value={t.title} onChange={(e) => setText(i, { title: e.target.value })} /></label>
          <label className="pp-field">Citation<input value={t.citation ?? ""} onChange={(e) => setText(i, { citation: e.target.value })} /></label>
          <label className="pp-field">Text<textarea rows={4} value={t.body} onChange={(e) => setText(i, { body: e.target.value })} /></label>
          <span className="ct-actions">
            <button type="button" className="btn sm" aria-label="Move up" disabled={i === 0}
                    onClick={() => { const n = [...texts]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setTexts(n); }}>↑</button>
            <button type="button" className="btn sm" aria-label="Move down" disabled={i === texts.length - 1}
                    onClick={() => { const n = [...texts]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; setTexts(n); }}>↓</button>
            <IconButton icon="x" size="row" danger label="Remove this text" onClick={() => setTexts(texts.filter((_, j) => j !== i))} />
          </span>
        </div>))}
      <div className="pp-actions">
        <button type="button" className="btn sm" onClick={() => setTexts([...texts, { title: "", citation: "", body: "" }])}>Add a text</button>
        <button type="button" className="btn primary sm" disabled={busy || !textsChanged}
                onClick={() => act(() => api.put(`${base}/texts`, { texts }))}>Save the texts</button>
      </div>
      {error && <p className="inc-error">{error}</p>}
    </section>
  );
}
